import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import axiosClient from '../../../../api/axiosClient';
import { tenantApi } from '../../../../api/tenantApi';
import { GroupScope } from '../../../../api/tenantGroupsApi';

export interface ScopeOption {
  code: string;
  name: string;
  /** name + code combined, so the picker stays searchable by either. */
  label: string;
}

/**
 * Loads the tenant / customer / sponsor datasets that back the tenant-access
 * pickers (React Query keys ['tenants'], ['lookup-customers'], ['lookup-sponsors'])
 * and exposes:
 *   - optionsFor(scope): Autocomplete options (rendered via <EntityLabel>)
 *   - nameFor(scope, code): resolve a stored code -> human name (undefined if unknown)
 *
 * These are the SAME datasets AccessGrantsDrawer uses, so names resolve
 * identically and an unknown/deleted code falls back to code-primary in
 * EntityLabel. Shared by GroupEditorDrawer + AssignedGroups so the name-first
 * treatment stays consistent across the Tenant Groups UI.
 */
export function useScopeEntities(enabled: boolean) {
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: () => tenantApi.getAll(),
    enabled,
  });
  const customersQuery = useQuery({
    queryKey: ['lookup-customers'],
    queryFn: async () => (await axiosClient.get('/api/lookup/customers')).data as any[],
    enabled,
  });
  const sponsorsQuery = useQuery({
    queryKey: ['lookup-sponsors'],
    queryFn: async () => (await axiosClient.get('/api/lookup/sponsors')).data as any[],
    enabled,
  });

  const nameByCode = useMemo(() => {
    const build = (rows: any[] | undefined, codeKey: string, nameKey: string) => {
      const m = new Map<string, string>();
      (rows ?? []).forEach((r: any) => {
        if (r?.[codeKey]) m.set(r[codeKey], (r[nameKey] ?? '').trim());
      });
      return m;
    };
    return {
      tenant: build(tenantsQuery.data as any[], 'tenant_code', 'tenant_name'),
      customer: build(customersQuery.data as any[], 'customer_code', 'customer_name'),
      sponsor: build(sponsorsQuery.data as any[], 'sponsor_code', 'sponsor_name'),
    } as Record<GroupScope, Map<string, string>>;
  }, [tenantsQuery.data, customersQuery.data, sponsorsQuery.data]);

  const optionsFor = useMemo(() => {
    const opt = (code: string, name?: string): ScopeOption => {
      const n = (name ?? '').trim();
      return { code, name: n, label: n ? `${n} · ${code}` : code };
    };
    const byScope: Record<GroupScope, ScopeOption[]> = {
      tenant: ((tenantsQuery.data as any[]) ?? []).map((t: any) => opt(t.tenant_code, t.tenant_name)),
      customer: ((customersQuery.data as any[]) ?? []).map((c: any) => opt(c.customer_code, c.customer_name)),
      sponsor: ((sponsorsQuery.data as any[]) ?? []).map((s: any) => opt(s.sponsor_code, s.sponsor_name)),
    };
    return (scope: GroupScope): ScopeOption[] => byScope[scope];
  }, [tenantsQuery.data, customersQuery.data, sponsorsQuery.data]);

  const nameFor = (scope: GroupScope, code: string): string | undefined => {
    const n = nameByCode[scope]?.get(code);
    return n && n.length > 0 ? n : undefined;
  };

  return {
    optionsFor,
    nameFor,
    loading: tenantsQuery.isLoading || customersQuery.isLoading || sponsorsQuery.isLoading,
    error: tenantsQuery.isError || customersQuery.isError || sponsorsQuery.isError,
  };
}
